import { Box, Tab, Tabs, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import OrganizationGeneral from './components/OrganizationGeneral';
import { OrganizationUsers } from './components/OrganizationUsers';
import Page, { StickyHeaderContent } from '../../components/util/Page';
import { getOrganizationAnalytics } from '../../redux/slices/organization';
import { useNavigate, useParams } from 'react-router';
import { PATH_DASHBOARD } from 'routes/paths';

import OrganizationSenders from './components/OrganizationSenders';
import useGuardedDispatch from 'guards/useGuardedDispatch';
import BrandedLinks from './components/BrandedLinks';
import { Button } from '@nps.today/component-library';
import Providers from './components/Providers';
import Icon from 'components/icons/Icon';
import DataPrivacy from './components/DataPrivacy';
import Relay from './components/Relay';
import HumanVerification from './components/HumanVerification';
import { useTranslator } from 'translation/useTranslator';
import Authentication from './components/authentication/Authentication';
import AccessGuard, { anyOf, rules, useAccess } from 'access';

export enum OrganisationTab {
  GENERAL = 'general',
  AUTHENTICATION = 'authentication',
  PROVIDERS = 'providers',
  USERS = 'users',
  BRANDING = 'branding',
  SENDERS = 'senders',
  DATA_PRIVACY = 'data_privacy',
  RELAY = 'relay',
  HUMAN_VERIFICATION = 'human_verification'
}

export default function Organization() {
  const translator = useTranslator();
  const guardedDispatch = useGuardedDispatch();
  const { tab = OrganisationTab.GENERAL } = useParams();
  const navigate = useNavigate();
  const canReadUsers = useAccess(anyOf(rules.user.read, rules.claimAssignment.any));
  const canReadTenants = useAccess(rules.tenant.read);
  const accessSenders = useAccess(rules.sender.read);
  const canAccessBranding = useAccess(
    anyOf(rules.brandedLink.create, rules.brandedLink.update, rules.brandedLink.delete)
  );

  useEffect(() => {
    guardedDispatch({
      action: getOrganizationAnalytics,
      rule: rules.tenant.read,
      disableSuccessMessage: true
    });
  }, [guardedDispatch]);

  const onTabChange = (newTab: OrganisationTab) => {
    navigate(`${PATH_DASHBOARD.organization.root}/${newTab}`);
  };

  //user header states
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openDeleteUsers, setOpenDeleteUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel | undefined>(undefined);

  //sender header state
  const [displayAddSender, setDisplayAddSender] = useState(false);

  //branded link header state
  const [displayAddBrandedLink, setDisplayAddBrandedLink] = useState(false);

  const selectedUsersCount = selectedUsers?.ids.size ?? 0;

  const TABS = useMemo(
    () => [
      ...(canReadTenants
        ? [
            {
              value: OrganisationTab.GENERAL,
              label: translator.general(),
              icon: <Icon type="general" />,
              component: <OrganizationGeneral />
            }
          ]
        : []),
      ...(canReadUsers
        ? [
            {
              value: OrganisationTab.USERS,
              label: translator.users(),
              icon: <Icon type="users" />,
              component: (
                <OrganizationUsers
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                  openAddUser={openAddUser}
                  setOpenAddUser={setOpenAddUser}
                  openDeleteUsers={openDeleteUsers}
                  setOpenDeleteUsers={setOpenDeleteUsers}
                />
              ),
              headerContent: [
                [
                  selectedUsersCount > 0 && (
                    <AccessGuard rule={rules.user.delete} key="deleteUsers">
                      <Button
                        dataTestId="delete-users"
                        color={'error'}
                        variant={'contained'}
                        onClick={() => setOpenDeleteUsers(true)}
                      >
                        {translator.deleteUsers(`${selectedUsersCount}`)}
                      </Button>
                    </AccessGuard>
                  ),
                  <AccessGuard rule={rules.user.create} key="addUser">
                    <Button
                      key="addUser"
                      dataTestId="add-user"
                      variant={'contained'}
                      onClick={() => setOpenAddUser(true)}
                    >
                      {translator.addUser()}
                    </Button>
                  </AccessGuard>
                ]
              ]
            }
          ]
        : []),
      ...(canAccessBranding
        ? [
            {
              value: OrganisationTab.BRANDING,
              label: translator.branding(),
              icon: <Icon type="link" />,
              rule: anyOf(
                rules.brandedLink.create,
                rules.brandedLink.update,
                rules.brandedLink.delete
              ),
              component: (
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {translator.brandedLinks()}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, maxWidth: 720 }}
                  >
                    {translator.brandedLinksTabDescription()}
                  </Typography>
                  <BrandedLinks
                    displayAddBrandedLink={displayAddBrandedLink}
                    setDisplayAddBrandedLink={setDisplayAddBrandedLink}
                  />
                </Box>
              ),
              headerContent: [
                [
                  <AccessGuard rule={rules.brandedLink.create} key="addBrandedLinkAccess">
                    <Button
                      dataTestId="add-branded-link"
                      key="addBrandedLink"
                      variant={'contained'}
                      onClick={() => setDisplayAddBrandedLink(true)}
                    >
                      {translator.addBrandedLink()}
                    </Button>
                  </AccessGuard>
                ]
              ]
            }
          ]
        : []),
      ...(accessSenders
        ? [
            {
              value: OrganisationTab.SENDERS,
              label: translator.senders(),
              icon: <Icon type="senders" />,
              component: (
                <OrganizationSenders
                  displayAddSender={displayAddSender}
                  setDisplayAddSender={setDisplayAddSender}
                />
              ),
              rule: rules.sender.read,
              headerContent: [
                [
                  <AccessGuard rule={rules.sender.create} key="addSenderAccess">
                    <Button
                      dataTestId="add-sender"
                      key="addSender"
                      variant={'contained'}
                      onClick={() => setDisplayAddSender(true)}
                    >
                      {translator.addSender()}
                    </Button>
                  </AccessGuard>
                ]
              ]
            }
          ]
        : []),
      {
        value: OrganisationTab.AUTHENTICATION,
        label: translator.authentication(),
        icon: <Icon type="key" />,
        component: <Authentication />
      },
      {
        value: OrganisationTab.PROVIDERS,
        label: translator.providers(),
        icon: <Icon type="providers" />,
        component: <Providers />
      },
      {
        value: OrganisationTab.DATA_PRIVACY,
        label: translator.dataPrivacy(),
        icon: <Icon type="dataretention" />,
        component: <DataPrivacy />
      },
      {
        value: OrganisationTab.RELAY,
        label: translator.relay(),
        icon: <Icon type="connection" />,
        component: <Relay />
      },
      {
        value: OrganisationTab.HUMAN_VERIFICATION,
        label: translator.humanVerification(),
        icon: <Icon type="robot" />,
        component: <HumanVerification />
      }
    ],
    [
      canReadTenants,
      canReadUsers,
      accessSenders,
      canAccessBranding,
      selectedUsers,
      selectedUsersCount,
      openAddUser,
      openDeleteUsers,
      displayAddSender,
      displayAddBrandedLink,
      translator
    ]
  );

  // Use the requested tab when accessible, otherwise default to the left-most tab the user can access.
  const activeTab = TABS.some((aTab) => aTab.value === tab) ? tab : TABS[0]?.value;
  const selectedTab = TABS.find((aTab) => aTab.value === activeTab);

  useEffect(() => {
    if (activeTab && activeTab !== tab) {
      navigate(`${PATH_DASHBOARD.organization.root}/${activeTab}`, { replace: true });
    }
  }, [activeTab, tab, navigate]);

  return (
    <Page
      title={translator.account()}
      stickyHeaderContent={selectedTab?.headerContent as StickyHeaderContent[][]}
    >
      <div style={{ display: 'inline-grid' }}>
        <Tabs
          value={activeTab}
          scrollButtons="auto"
          variant="scrollable"
          allowScrollButtonsMobile
          onChange={(_, value) => onTabChange(value as OrganisationTab)}
        >
          {TABS.map((tabItem) => (
            <Tab
              disableRipple
              key={tabItem.value}
              label={
                <AccessGuard rule={tabItem.rule} displayAccessTag="TopRight">
                  {tabItem.label}
                </AccessGuard>
              }
              icon={tabItem.icon}
              value={tabItem.value}
            />
          ))}
        </Tabs>
      </div>
      <Box sx={{ mb: 5 }} />
      {TABS.map((tabItem) => {
        const isMatched = tabItem.value === activeTab;
        return (
          isMatched && (
            <AccessGuard key={tabItem.value} rule={tabItem.rule} displayNoAccess={true}>
              <Box>{tabItem.component}</Box>
            </AccessGuard>
          )
        );
      })}
    </Page>
  );
}
